// @flow
import { PDFFillerContext } from 'pages/Contexts/PdfFillerContext'
import * as React from 'react'
import { useDrag } from 'react-dnd'
import { ResizableBox } from 'react-resizable'
import styled from 'styled-components'
import type { FontSize, InputField, InputType } from 'types/input-field'
import type { ViewMode } from 'types/pdf-filler-context'
import Theme from 'ui/theme'

import { MIN_HEIGHT, MIN_WIDTH } from './constants'
import getInputComponent from './InputFactory'

type Props = {
  inputField: InputField,
  toggleInputObjectIsResizing: (id: string, isResizing: boolean) => void,
  resourceId: string,
}

const DraggableWrapper = styled.div`
  position: absolute;
  top: ${(props: InputField) => props.top}px;
  left: ${(props: InputField) => props.left}px;
`
const CustomResizeHandle = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 3px solid ${Theme.color.borderPrimary};
  position: absolute;
  bottom: -15px;
  right: -15px;
  cursor: se-resize;
`

type StyledInputWrapperProps = {
  $disabled?: boolean,
  $error?: boolean,
  $fontSize: number,
  $isDragging?: boolean,
  $isFilling: boolean,
  $viewMode: ViewMode,
  active?: boolean,
  $lineHeight: number,
  $officeUse?: boolean,
}

const StyledInputWrapper = styled.div`
  width: 100%;
  height: 100%;
  cursor: ${(props: StyledInputWrapperProps) =>
    props.$isFilling ? 'text' : 'default'};
  border-width: ${Theme.width.border};
  border-radius: ${Theme.spacing.xsmall};
  border-color: ${(props: StyledInputWrapperProps) => {
    if (props.$error) {
      return Theme.color.error
    }

    if (props.active) {
      return Theme.color.borderPrimary
    }

    if (props.$viewMode === 'response') {
      return 'transparent'
    }

    return Theme.color.borderPrimary
  }};
  background-color: ${(props: StyledInputWrapperProps) => {
    if (props.$error) {
      return Theme.color.errorTransparent
    }

    if (props.$viewMode === 'response') {
      return 'transparent'
    }

    if (props.$isFilling && props.$disabled) {
      return Theme.color.borderSecondary
    }

    if (props.$isDragging) {
      return Theme.color.pdfInput
    }

    if (props.active) {
      return Theme.color.main
    }

    if (props.$officeUse) {
      return Theme.color.pdfInputOfficeUseTransparent
    }

    return Theme.color.pdfInputTransparent
  }};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;

  ${(props: StyledInputWrapperProps) =>
    props.$fontSize ? `font-size: ${props.$fontSize}pt;` : ''}

  ${(props: StyledInputWrapperProps) =>
    props.$lineHeight ? `font-size: ${props.$lineHeight}pt;` : ''}
`

const RequiredAsterisk = styled.div.attrs({ children: '*' })`
  color: ${Theme.color.error};
  font-size: ${(props: { $fontSize: number }) => props.$fontSize}px;
  position: absolute;
  top: 0;
  right: 0;
  line-height: 0.8;
`

function getFontSize(inputField: InputField, zoom: number) {
  const multiplier = inputField.input_type === 'date' ? 0.6 : 1
  const autoFontSize = Math.floor(inputField.height / 2) * zoom * multiplier

  const fontSize = Number(inputField.font_size) * zoom || autoFontSize

  return [fontSize, autoFontSize]
}

function FormInput({
  inputField,
  toggleInputObjectIsResizing,
  resourceId,
}: Props): React.Node {
  const {
    errors,
    inputRefs,
    selectedInputFieldId,
    setAnswers,
    setErrors,
    setInputFields,
    setInputRefs,
    setIsDragging,
    setLastAppliedSettings,
    setSelectedInputFieldId,
    setOverlayActive,
    submitting,
    viewMode,
    zoom,
  } = React.useContext(PDFFillerContext)

  const inputRef = React.useRef<null | HTMLInputElement>(null)
  const [draggingThis, setDraggingThis] = React.useState(false) // local version of isDragging

  React.useEffect(() => {
    if (inputRef.current && !inputRefs.includes(inputRef)) {
      setInputRefs((prevInputRefs) => {
        return [...prevInputRefs, inputRef]
      })
    }
  }, [inputRefs, setInputRefs])

  const isFilling = !['edit', 'response'].includes(viewMode)

  const disabled = React.useMemo(() => {
    if (viewMode === 'office-use') {
      return !inputField.is_office_use
    }

    if (isFilling && inputField.is_office_use) {
      return true
    }

    return !isFilling
  }, [inputField.is_office_use, isFilling, viewMode])

  const [, drag] = useDrag(
    () => ({
      canDrag: () => !inputField.isResizing && viewMode === 'edit',
      end: () => {
        setIsDragging(false)
        setDraggingThis(false)
      },
      item: () => {
        // called at the beginning of the drag
        setIsDragging(true)
        setDraggingThis(true)
        setSelectedInputFieldId(inputField.id)
        return inputField
      },
      type: 'FORM_INPUT',
    }),
    [inputField]
  )

  const { font_size: inputFontSize, id, input_type, left, top } = inputField

  const handleResizeStart = React.useCallback(() => {
    setSelectedInputFieldId(id)
    toggleInputObjectIsResizing(id, true)
  }, [setSelectedInputFieldId, id, toggleInputObjectIsResizing])

  const handleResizeStop = React.useCallback(
    async (event, data) => {
      toggleInputObjectIsResizing(id, false)

      const width = data.size.width / zoom
      const height = data.size.height / zoom

      setInputFields((prevInputFields) => {
        return {
          ...prevInputFields,
          [id]: {
            ...prevInputFields[id],
            width,
            height,
          },
        }
      })

      setLastAppliedSettings((previousAppliedSettings) => {
        const newAppliedSettings: {
          [InputType]: { font_size: FontSize, width: number, height: number },
        } = {}

        newAppliedSettings[input_type] = {
          font_size: inputFontSize,
          width,
          height,
        }

        return { ...previousAppliedSettings, ...newAppliedSettings }
      })
    },
    [
      toggleInputObjectIsResizing,
      id,
      zoom,
      setInputFields,
      setLastAppliedSettings,
      input_type,
      inputFontSize,
    ]
  )

  const handleInputChange = React.useCallback(
    async (event: SyntheticEvent<HTMLInputElement>) => {
      const value =
        inputField.input_type === 'checkbox'
          ? event.currentTarget.checked.toString()
          : event.currentTarget.value

      setAnswers((prevAnswers) => {
        return {
          ...prevAnswers,
          [id]: {
            answer_raw: value,
            input_field: id,
            response: resourceId,
          },
        }
      })

      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          [id]: '',
        }
      })
    },
    [id, inputField, resourceId, setAnswers, setErrors]
  )

  const handleInputClick = React.useCallback(() => {
    if (viewMode === 'response') return

    setSelectedInputFieldId(inputField.id)
    setOverlayActive(null)
  }, [inputField.id, setOverlayActive, setSelectedInputFieldId, viewMode])

  const isActive = React.useMemo(
    () => selectedInputFieldId === inputField.id,
    [inputField, selectedInputFieldId]
  )

  const handleInputFocus = React.useCallback(() => {
    setSelectedInputFieldId(id)
  }, [id, setSelectedInputFieldId])

  const InputComponent = getInputComponent(inputField.input_type)

  const [fontSize, autoFontSize] = getFontSize(inputField, zoom)

  const itemTop = top * zoom
  const itemLeft = left * zoom
  const itemHeight = inputField.height * zoom
  const itemWidth = inputField.width * zoom

  const [variableHeight, setVariableHeight] = React.useState(inputField.height)

  return (
    <DraggableWrapper
      left={itemLeft}
      onClick={handleInputClick}
      ref={drag}
      top={itemTop}
    >
      <ResizableBox
        handle={draggingThis || !isActive ? <div /> : <CustomResizeHandle />}
        height={
          ['response', 'edit'].includes(viewMode)
            ? itemHeight
            : variableHeight * zoom
        }
        lockAspectRatio={inputField.input_type === 'checkbox'}
        minConstraints={[MIN_WIDTH, MIN_HEIGHT]}
        onResizeStart={handleResizeStart}
        onResizeStop={handleResizeStop}
        resizeHandles={viewMode === 'edit' ? ['se'] : []}
        style={{
          minHeight: ['fill', 'preview'].includes(viewMode)
            ? itemHeight
            : 'initial',
        }}
        width={itemWidth}
      >
        <>
          <StyledInputWrapper
            $disabled={disabled}
            $error={Boolean(errors[inputField.id])}
            $fontSize={fontSize}
            $isDragging={draggingThis}
            $isFilling={isFilling}
            $lineHeight={fontSize}
            $officeUse={inputField.is_office_use}
            $viewMode={viewMode}
            active={isActive}
            id={inputField.id}
          >
            <InputComponent
              data-input-field={inputField.id}
              data-response-id={resourceId}
              disabled={disabled || submitting}
              fontSize={fontSize}
              innerRef={inputRef}
              inputField={inputField}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              setVariableHeight={setVariableHeight}
              variableHeight={variableHeight}
            />
          </StyledInputWrapper>
          {inputField.required && <RequiredAsterisk $fontSize={autoFontSize} />}
        </>
      </ResizableBox>
    </DraggableWrapper>
  )
}

export default FormInput
